import type maplibregl from "maplibre-gl";
import { mapStyleReady } from "../../shared/map-style-safe.ts";
import type { LngLat } from "../../shared/types.ts";
import { setHomeQuestHighlight } from "../quest/quest-spot-map-layers.ts";

/**
 * Raised start marker for the selected home quest (sits above clusters).
 * Drawn as a MapLibre circle layer so it stays glued to the map while panning.
 */
export class QuestHighlight {
  private id: string | null = null;
  private at: LngLat | null = null;
  private placeGen = 0;
  private readonly map: maplibregl.Map;
  private readonly onChange: (id: string | null) => void;

  constructor(map: maplibregl.Map, onChange: (id: string | null) => void) {
    this.map = map;
    this.onChange = onChange;
  }

  currentId(): string | null {
    return this.id;
  }

  reconcile(): void {
    if (this.id && this.at) this.show(this.id, this.at);
    else this.clear();
  }

  show(id: string | null, at: LngLat | null): void {
    if (!id || !at || !Number.isFinite(at[0]) || !Number.isFinite(at[1])) {
      this.clear();
      return;
    }
    this.at = at;
    const gen = ++this.placeGen;
    this.id = id;
    const live = (): boolean => gen === this.placeGen && this.id === id;

    const place = (retry: boolean): void => {
      if (!live()) return;
      if (!mapStyleReady(this.map)) {
        if (!retry) {
          this.map.once("idle", () => requestAnimationFrame(() => place(true)));
        }
        return;
      }
      try {
        setHomeQuestHighlight(this.map, at);
        if (!live()) return;
        this.onChange(id);
      } catch {
        if (!retry && live()) {
          this.map.once("idle", () => requestAnimationFrame(() => place(true)));
          return;
        }
        if (live()) this.clear();
      }
    };

    const whenStill = (): void => {
      if (!live()) return;
      if (this.map.isMoving() || this.map.isEasing()) {
        this.map.once("moveend", () => requestAnimationFrame(() => place(false)));
        return;
      }
      place(false);
    };

    if (mapStyleReady(this.map)) requestAnimationFrame(whenStill);
    else this.map.once("idle", () => requestAnimationFrame(whenStill));
  }

  clear(): void {
    this.placeGen += 1;
    this.id = null;
    this.at = null;
    setHomeQuestHighlight(this.map, null);
    this.onChange(null);
  }
}
